import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import Tooltip from "@components/Tooltip";
import Table from "@components/Table";
import { OPERATION_STATUS, ROUTES } from "@src/constants";
import { Column, Row } from "@src/components/Flex";
import Text from "@src/components/Text";
import SquareTokenIcon from "@components/SquareTokenIcon";
import SizedBox from "@components/SizedBox";
import Button from "@src/components/Button";
import { useStores } from "@stores";
import { observer } from "mobx-react-lite";
import BN from "@src/utils/BN";
import Skeleton from "react-loading-skeleton";
import { useTheme } from "@emotion/react";
import { TPoolStats } from "@src/stores/LendStore";
import { tokensUnderlying } from "@src/constants/tokensUnderlying";


interface IProps { }

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const SupplyApy = styled.div`
  display: flex;
  white-space: nowrap;

  img {
    margin: 1px 4px 0 -1px;
  }
`;

const TooltipText = styled(Text)`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  max-width: 180px;
  white-space: normal;
`;

const StyledAsset = styled.img<{ index: number }>`
  width: 31px;
  height: 31px;
  border-radius: 100px;
  margin-right: -${(props) => props.index + 10}px;
`;

const TooltipIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 100px;
`;

const DesktopTable: React.FC<IProps> = () => {
  const { marketId } = useParams<{ marketId: string }>();
  const [assets, setAssets] = useState<any[]>([]);
  const { lendStore } = useStores();
  const theme = useTheme();

  const navigate = useNavigate();

  const isSupplyDisabled = useCallback((token: TPoolStats) => {
    if (token?.supplyLimit.eq(0)) return false;
    if (!token?.totalSupply || !token?.totalBorrow) return false;
    const reserves = BN.formatUnits(
      token?.totalSupply?.minus(token?.totalBorrow),
      token?.decimals
    );

    return reserves.gt(token?.supplyLimit.div(token.prices.min));
  }, []);

  const columns = useMemo(
    () => [
      { Header: "Asset", accessor: "asset" },
      {
        Header: () => (
          <Row
            style={{ cursor: "pointer" }}
            justifyContent="flex-end"
          >
            <Tooltip
              content={
                <Text textAlign="left">
                  Amount of deposited tokens in total.
                </Text>
              }
            >
              <Text size="medium" nowrap textAlign="right">
                Total supply
              </Text>
            </Tooltip>
            <img
              src={theme.images.icons.group}
              alt="group"
              className="sort-icon"
            />
          </Row>
        ),
        accessor: "supply"
      },
      {
        Header: () => (
          <Row
            style={{ cursor: "pointer" }}
            justifyContent="flex-end"
          >
            <Tooltip
              content={
                <Text textAlign="left">
                  Annualized interest earned by depositors
                </Text>
              }
            >
              <Text size="medium" nowrap textAlign="right">
                Supply APY
              </Text>
            </Tooltip>
            <img
              src={theme.images.icons.group}
              alt="group"
              className="sort-icon"
            />
          </Row>
        ),
        accessor: "supplyApy"
      },
      {
        Header: () => (
          <Row
            style={{ cursor: "pointer" }}
            justifyContent="flex-end"
          >
            <Tooltip
              content={
                <Text textAlign="left">
                  Amount of borrowed tokens in total.
                </Text>
              }
            >
              <Text size="medium" nowrap textAlign="right">
                Total borrow
              </Text>
            </Tooltip>
            <img
              src={theme.images.icons.group}
              alt="group"
              className="sort-icon"
            />
          </Row>
        ),
        accessor: "borrow"
      },
      {
        Header: () => (
          <Row
            style={{ cursor: "pointer" }}
            justifyContent="flex-end"
          >
            <Tooltip
              content={
                <Text textAlign="left">
                  Annual interest paid by borrowers taking into account
                  compounding.
                </Text>
              }
            >
              <Text size="medium" nowrap textAlign="right">
                Borrow APR
              </Text>
            </Tooltip>
            <img
              src={theme.images.icons.group}
              alt="group"
              className="sort-icon"
            />
          </Row>
        ),
        accessor: "borrowApy"
      },
      { Header: "", accessor: "borrowBtn" },
      { Header: "", accessor: "supplyBtn" }
    ],
    [theme.images.icons.group]
  );

  const openModal = useCallback(
    (
      e: React.MouseEvent,
      marketId: string,
      operationName: string,
      assetId: string
    ) => {
      e.stopPropagation();
      console.log("openModal");
      return navigate(`/${marketId}/${operationName}/${assetId}`);
    },
    [navigate]
  );

  useMemo(() => {
    let data: any = lendStore.poolsStats
      .slice()
      .filter((a) => a.paused.eq(0))

    data = data.map((s: TPoolStats) => ({
      onClick: () => {
        navigate(
          ROUTES.MARKET_TOKEN_DETAILS.replace(
            ":marketId",
            lendStore.pool.address
          ).replace(":assetId", s.assetId)
        );
      },
      asset: (
        <Row alignItems="center">
          <SquareTokenIcon size="small" src={s.logo} alt="logo" />
          <SizedBox width={16} />
          <Column>
            <Text size="small">{s.symbol}</Text>
            <Text type="secondary" size="small" fitContent>
              ${s.prices.min.toBigFormat(4)}
            </Text>
          </Column>
        </Row>
      ),
      underlying: (
        <Tooltip
          config={{
            placement: "top"
          }}
          content={
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                width: 300
              }}
            >
              {s?.underlying &&
                s?.underlying.map((el, key) => {
                  const token =
                    tokensUnderlying[el as keyof typeof tokensUnderlying];
                  return (
                    <Row style={{ flex: 1, minWidth: "50%", gap: 5 }}>
                      <TooltipIcon
                        style={{ width: 20 }}
                        src={typeof token === "string" ? token : token.icon}
                      />
                      <Text textAlign="left">
                        {typeof token === "string"
                          ? token
                          : token.name.length > 10
                            ? `${token.name.substring(0, 10)}...`
                            : token.name}
                      </Text>
                    </Row>
                  );
                })}
            </div>
          }
        >
          <Row>
            {s?.underlying &&
              s?.underlying.map((el, key) => {
                const token =
                  tokensUnderlying[el as keyof typeof tokensUnderlying];
                return (
                  <StyledAsset
                    index={key}
                    src={typeof token === "string" ? token : token.icon}
                  />
                );
              })}
          </Row>
        </Tooltip>
      ),
      supply:
        s.symbol === "?ROME" ? (
          <div></div>
        ) : (
          <Column crossAxisSize="max">
            <Text weight={500} textAlign="right" size="medium">
              {BN.formatUnits(s.totalSupply, s.decimals).toBigFormat(2) +
                ` ${s.symbol}`}
            </Text>
            <Text textAlign="right" size="small" type="secondary">
              ${""}
              {BN.formatUnits(s.totalSupply, s.decimals)
                .times(s.prices.min)
                .toBigFormat(2)}
            </Text>
          </Column>
        ),
      supplyApy:
        (lendStore.poolName === "Axly Market" &&
          s.symbol.indexOf("_") !== -1) ||
          s.symbol === "?ROME" ? (
          <div></div>
        ) : (
          <SupplyApy>
            <Tooltip
              config={{ placement: "top-start" }}
              content={
                <TooltipText textAlign="left">
                  Non-borrowed tokens are automatically staked within native
                  protocol to generate additional interest
                </TooltipText>
              }
            >
              {s.isAutostakeAvl && (
                <img
                  src={theme.images.icons.autostaking}
                  alt="autostaking"
                  className="autostaking-icon"
                />
              )}
            </Tooltip>
            {s.supplyAPY.toFormat(2) + "%"}
          </SupplyApy>
        ),
      borrow:
        lendStore.poolName === "Axly Market" && s.symbol.indexOf("_") !== -1 ? (
          <div></div>
        ) : (
          <Column crossAxisSize="max">
            <Text weight={500} textAlign="right" size="medium">
              {BN.formatUnits(s.totalBorrow, s.decimals).toBigFormat(2) +
                ` ${s.symbol}`}
            </Text>
            <Text textAlign="right" size="small" type="secondary">
              ${""}
              {BN.formatUnits(s.totalBorrow, s.decimals)
                .times(s.prices.min)
                .toBigFormat(2)}
            </Text>
          </Column>
        ),
      borrowApy:
        lendStore.poolName === "Axly Market" && s.symbol.indexOf("_") !== -1 ? (
          <div></div>
        ) : (
          s.borrowAPR.toBigFormat(2) + "%"
        ),
      borrowBtn:
        lendStore.poolName === "Axly Market" ? (
          <div></div>
        ) : s.symbol === "?ROME" ? (
          <Button
            kind="secondary"
            size="medium"
            fixed
            onClick={(e) => {
              lendStore.setOperationType(OPERATION_STATUS.MINT);
              openModal(e, lendStore.marketId, "borrow", s.assetId);
            }}
            style={{ width: "100px", margin: "0 auto" }}
          >
            Mint
          </Button>
        ) : (
          <Button
            kind="secondary"
            size="medium"
            fixed
            onClick={(e) =>
              openModal(e, lendStore.marketId, "borrow", s.assetId)
            }
            style={{ width: "100px", margin: "0 auto" }}
          >
            Borrow
          </Button>
        ),
      supplyBtn:
        s.symbol === "?ROME" ? (
          <Button
            kind="secondary"
            size="medium"
            fixed
            disabled={false}
            onClick={(e) =>
              window.open(
                "https://medium.com/@puzzlenetwork/all-roads-lead-to-rome-72a3c47587b9"
              )
            }
            style={{ width: "100px", margin: "0 auto" }}
          >
            Read more
          </Button>
        ) : isSupplyDisabled(s) ? (
          <Tooltip
            content={
              <Text textAlign="left">Maximum total supply is reached</Text>
            }
          >
            <Button
              kind="secondary"
              size="medium"
              fixed
              disabled={true}
              onClick={(e) =>
                openModal(e, lendStore.marketId, "supply", s.assetId)
              }
              style={{ width: "100px", margin: "0 auto" }}
            >
              Supply
            </Button>
          </Tooltip>
        ) : lendStore.poolName === "Axly Market" &&
          s.symbol.indexOf("_") !== -1 ? (
          <Button
            kind="secondary"
            size="medium"
            fixed
            onClick={(e) => window.open("https://app.axly.io/")}
            style={{ width: "100px", margin: "0 auto" }}
          >
            Axly
          </Button>
        ) : (
          <Button
            kind="secondary"
            size="medium"
            fixed
            onClick={(e) =>
              openModal(e, lendStore.marketId, "supply", s.assetId)
            }
            style={{ width: "100px", margin: "0 auto" }}
          >
            Supply
          </Button>
        )
    }));
    setAssets(data);
  }, [
    marketId,
    lendStore.poolsStats,
    theme.images.icons.autostaking,
    isSupplyDisabled,
    navigate,
    openModal
  ]);

  return (
    <Root >
      {lendStore.initialized && assets.length ? (
        <Table columns={columns} data={assets} />
      ) : (
        <Skeleton height={56} style={{ marginBottom: 8 }} count={4} />
      )}
    </Root>
  );
};
export default observer(DesktopTable);
